import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Flame } from "lucide-react";
import { errMsg, post } from "@/lib/api";

const n = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

export function ShipParticulars({ v }) {
  const qc = useQueryClient();
  const p = v.particulars;
  const enrich = useMutation({
    mutationFn: () => post(`/live/vessels/${v.mmsi}/enrich`),
    onSuccess: (d) => { toast.success(`Details stored for IMO ${d.imo}`); ["live-vessels", "live-ports"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error("Could not fetch details", { description: errMsg(e) }),
  });
  return (
    <div className="mt-4 rounded-lg border border-white/10 p-3" data-testid="ship-particulars">
      <div className="mb-2 flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Particulars</span>{p && <span className="font-mono text-[10px] text-slate-500">{p.source}</span>}</div>
      {p ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
          <dt className="text-slate-500">Gross tonnage</dt><dd className="text-right text-slate-100" data-testid="ship-gt">{n(p.gross_tonnage)}</dd>
          <dt className="text-slate-500">Deadweight</dt><dd className="text-right text-slate-100">{p.deadweight_t ? `${n(p.deadweight_t)} t` : "—"}</dd>
          <dt className="text-slate-500">Engine</dt><dd className="text-right text-slate-100" data-testid="ship-engine" title={p.engine_status || ""}>{p.engine_kw ? `${n(p.engine_kw)} kW${p.engine_designation ? ` · ${p.engine_designation}` : ""}` : p.engine_status || "not on file"}</dd>
          <dt className="text-slate-500">Built</dt><dd className="text-right text-slate-100">{p.year_built ?? "—"}</dd>
          <dt className="text-slate-500">Home port</dt><dd className="text-right text-slate-100">{p.home_port || "—"}</dd>
        </dl>
      ) : (
        <button data-testid="ship-detail-enrich" disabled={!v.imo || enrich.isPending} onClick={() => enrich.mutate()} title={v.imo ? "" : "No IMO broadcast; cannot look up"}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-lime-400/40 py-1.5 text-xs text-lime-200 transition-colors hover:bg-lime-400/10 disabled:opacity-40">
          <Download className="h-3.5 w-3.5" />{enrich.isPending ? "Fetching…" : v.imo ? "Fetch tonnage & details · 1 Datalastic credit" : "No IMO — details unavailable"}
        </button>
      )}
    </div>
  );
}

export function ShipEmissions({ e }) {
  if (!e) return null;
  return (
    <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.04] p-3" data-testid="ship-emissions">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"><Flame className="h-3 w-3" />Estimated emissions now</span>
        <span className="rounded bg-amber-500/15 px-1.5 font-mono text-[10px] text-amber-300" data-testid="ship-emissions-basis">ESTIMATE</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5 text-center">
        {[["CO₂", e.co2_kg_h, 0, "co2"], ["NOₓ", e.nox_kg_h, 1, "nox"], ["SOₓ", e.sox_kg_h, 2, "sox"], ["PM", e.pm_kg_h, 2, "pm"]].map(([l, val, d, k]) => (
          <div key={k} className="rounded-md bg-black/20 py-1.5"><div className="text-[10px] text-slate-500">{l}</div><div className="text-sm font-semibold text-slate-100" data-testid={`ship-em-${k}`}>{n(val, d)}</div><div className="text-[9px] text-slate-500">kg/h</div></div>
        ))}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
        <dt className="text-slate-500">Mode</dt><dd className="text-right text-slate-300">{e.mode}</dd>
        <dt className="text-slate-500">Fuel</dt><dd className="text-right text-slate-300" data-testid="ship-em-fuel">{e.fuel} · {e.sulphur_pct}% S</dd>
        <dt className="text-slate-500">Power now</dt><dd className="text-right text-slate-300">{n(e.power_kw_now)} kW</dd>
        <dt className="text-slate-500">Main engine</dt><dd className="text-right text-slate-300">{n(e.main_engine_kw)} kW</dd>
      </dl>
      <p className="mt-2 text-[10px] leading-snug text-slate-500">{e.method}. GT {n(e.gt_used)} ({e.gt_basis}); power {e.power_basis}; fuel {e.fuel_basis}. Not a measurement and not a regulatory finding.</p>
    </div>
  );
}
